import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { UserSidebarSection } from '@/state/project';

import { SECTION_META } from './sectionConfig';

type Props = {
  section: UserSidebarSection;
  onSelect: (section: UserSidebarSection) => void;
  isAdmin?: boolean;
};

export default function UserSidebarNav({ section, onSelect, isAdmin }: Props) {
  const sections = SECTION_META.filter((m) => m.id !== 'admin' || isAdmin);

  return (
    <>
      {/* Mobile: evenly-spaced icon bar (labels via aria-label + title) */}
      <div className="flex items-center gap-1 border-b border-border bg-muted/30 p-2 sm:hidden">
        {sections.map(({ id, label, icon: Icon, disabled }) => {
          const isActive = section === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => !disabled && onSelect(id)}
              aria-label={label}
              title={disabled ? `${label} (soon)` : label}
              aria-current={isActive ? 'page' : undefined}
              aria-disabled={disabled || undefined}
              className={cn(
                'flex h-9 flex-1 items-center justify-center rounded-md transition-colors',
                disabled
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>

      {/* Desktop: vertical rail */}
      <TooltipProvider>
        <nav
          aria-label="Settings sections"
          className="hidden w-36 shrink-0 border-r border-border bg-muted/30 p-3 sm:flex sm:flex-col sm:gap-1"
        >
          {sections.map(({ id, label, icon: Icon, disabled }) => {
            const isActive = section === id;
            const button = (
              <button
                key={id}
                type="button"
                onClick={() => !disabled && onSelect(id)}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={disabled || undefined}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left',
                  disabled
                    ? 'text-muted-foreground/50 cursor-not-allowed'
                    : isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            );

            if (!disabled) return button;

            return (
              <Tooltip key={id}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">Coming soon</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      </TooltipProvider>
    </>
  );
}
